import { typedSupabase } from '@/lib/typed-supabase';
import {
  IService,
  ServiceResponse,
  createErrorResponse,
  registerService,
} from '@/services/core/service-registry';
import type {
    NotificationCategory,
    NotificationPreferences,
    NotificationPreferencesUpdate,
} from "@/types/notificationPreferences";
import { logger } from "@/lib/logger";

/**
 * Notification Preferences Service - Phase 4 compliant
 * Manages user notification preferences
 */
export class NotificationPreferencesService implements IService {
  readonly name = "notification-preferences";
  private initialized = false;

  async initialize(): Promise<void> {
    this.initialized = true;
  }

  async dispose(): Promise<void> {
    this.initialized = false;
  }

  /**
   * Get user's notification preferences
   */
  async getUserPreferences(
    userId?: string
  ): Promise<ServiceResponse<NotificationPreferences>> {
    try {
      let targetUserId = userId;

      if (!targetUserId) {
        try {
          const {
            data: { user },
            error: authError,
          } = await typedSupabase.auth.getUser();

          if (authError || !user) {
            logger.warn("Authentication issue in getUserPreferences, attempting session recovery...", { error: authError });

            const { data: sessionData, error: refreshError } =
              await typedSupabase.auth.refreshSession();

            if (refreshError || !sessionData.session?.user) {
              return createErrorResponse("Authentication failed. Please sign in again to continue.");
            }

            logger.info("✅ Session recovered successfully for getUserPreferences");
            targetUserId = sessionData.session.user.id;
          } else {
            targetUserId = user.id;
          }
        } catch (_sessionError) {
          return createErrorResponse("Authentication failed. Please sign in again to continue.");
        }
      }

      const { data, error } = await typedSupabase
        .from("notification_preferences")
        .select("*")
        .eq("user_id", targetUserId)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          const defaults = await this.createDefaultPreferences(targetUserId);
          if (defaults.success) {
            return { success: true, data: defaults.data! };
          }
          return { success: false, error: "Failed to create default preferences" };
        }
        logger.error("Database error getting preferences:", { error: error });
        return { success: false, error: error.message };
      }

      return { success: true, data: data as unknown as NotificationPreferences };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to get user preferences"
      };
    }
  }

  /**
   * Create default notification preferences for a user
   */
  async createDefaultPreferences(
    userId: string
  ): Promise<ServiceResponse<NotificationPreferences>> {
    try {
      try {
        const {
          data: { user },
          error: authError,
        } = await typedSupabase.auth.getUser();

        if (authError || !user || user.id !== userId) {
          logger.warn("🔐 Authentication issue in createDefaultPreferences, attempting session recovery...");

          const { data: sessionData, error: refreshError } =
            await typedSupabase.auth.refreshSession();

          if (refreshError || !sessionData.session) {
            return { success: false, error: "Authentication failed. Please sign in again to continue." };
          }

          logger.info("✅ Session recovered successfully for notification preferences");
        }
      } catch (_sessionError) {
        return { success: false, error: "Authentication failed. Please sign in again to continue." };
      }

      const { data, error } = await typedSupabase
        .from("notification_preferences")
        .insert({
          user_id: userId,
        })
        .select()
        .single();

      if (error) {
        logger.error("Database error creating preferences:", { error: error });
        return { success: false, error: error.message };
      }
      return { success: true, data: data as unknown as NotificationPreferences };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to create default preferences"
      };
    }
  }

  /**
   * Update user's notification preferences
   */
  async updatePreferences(updates: NotificationPreferencesUpdate): Promise<ServiceResponse<NotificationPreferences>> {
    try {
      const {
        data: { user },
        error: authError,
      } = await typedSupabase.auth.getUser();

      if (authError || !user) {
        logger.error("Authentication error updating preferences", { error: authError });
        return { success: false, error: "User not authenticated" };
      }

      logger.info("Updating preferences for user", { userId: user.id, updates });

      const { data, error } = await typedSupabase
        .from("notification_preferences")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        } as never)
        .eq("user_id", user.id)
        .select()
        .single();

      if (error) {
        logger.error("Database error updating preferences:", { error: error });
        return { success: false, error: error.message };
      }

      return { success: true, data: data as unknown as NotificationPreferences };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to update preferences"
      };
    }
  }

  /**
   * Get notification preferences with defaults
   */
  async getPreferencesWithDefaults(
    userId?: string
  ): Promise<ServiceResponse<NotificationPreferences>> {
    const response = await this.getUserPreferences(userId);

    if (response.error) {
      return { success: false, error: response.error };
    }

    const preferences = response.data;
    const defaults: NotificationPreferences = {
      id: "",
      user_id: userId ?? "",
      task_assignments: true,
      task_status_changes: true,
      task_due_reminders: true,
      task_overdue_alerts: true,
      task_comments: true,
      task_mentions: true,
      task_priority_changes: true,
      task_attachments: true,
      subtask_completions: true,
      post_likes: true,
      post_comments: true,
      comment_replies: true,
      community_mentions: true,
      role_changes: true,
      system_announcements: true,
      admin_actions: true,
      xp_milestones: true,
      streak_bonuses: true,
      achievement_badges: true,
      leaderboard_updates: true,
      season_start: true,
      season_end: true,
      season_weekly_updates: true,
      season_rank_changes: true,
      email_notifications: true,
      push_notifications: true,
      discord_notifications: false,
      discord_webhook_url: undefined,
      whatsapp_notifications: false,
      whatsapp_phone_number: undefined,
      telegram_notifications: false,
      telegram_chat_id: undefined,
      slack_notifications: false,
      slack_team_id: undefined,
      slack_user_id: undefined,
      digest_frequency: "daily",
      toast_duration: 3000,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    return { success: true, data: preferences ?? defaults };
  }

  /**
   * Check if user should receive a specific notification category
   */
  async shouldSendNotification(
    userId: string,
    category: NotificationCategory
  ): Promise<ServiceResponse<boolean>> {
    try {
      const { data, error } = await typedSupabase.rpc(
        "should_send_notification",
        {
          target_user_id: userId,
          notification_category: category,
        }
      );

      if (error) {
        logger.error("Error checking notification preference:", { error: error });
        return { success: true, data: true };
      }
      return { success: true, data: data as boolean };
    } catch (error) {
      logger.error("Error checking notification preference:", { error: error });
      return { success: true, data: true };
    }
  }

  /**
   * Bulk toggle preferences for a category
   */
  async toggleCategoryPreferences(
    category: "tasks" | "community" | "admin" | "achievements",
    enabled: boolean
  ): Promise<ServiceResponse<NotificationPreferences>> {
    try {
      const {
        data: { user },
      } = await typedSupabase.auth.getUser();
      if (!user) {
        return { success: false, error: "User not authenticated" };
      }

      let updates: Partial<NotificationPreferencesUpdate> = {};

      switch (category) {
        case "tasks":
          updates = {
            task_assignments: enabled,
            task_status_changes: enabled,
            task_due_reminders: enabled,
            task_overdue_alerts: enabled,
            task_comments: enabled,
            task_mentions: enabled,
            task_priority_changes: enabled,
            task_attachments: enabled,
            subtask_completions: enabled,
          };
          break;
        case "community":
          updates = {
            post_likes: enabled,
            post_comments: enabled,
            comment_replies: enabled,
            community_mentions: enabled,
          };
          break;
        case "admin":
          updates = {
            role_changes: enabled,
            system_announcements: enabled,
            admin_actions: enabled,
          };
          break;
        case "achievements":
          updates = {
            xp_milestones: enabled,
            streak_bonuses: enabled,
            achievement_badges: enabled,
            leaderboard_updates: enabled,
          };
          break;
      }

      return await this.updatePreferences(updates);
    } catch (error) {
      logger.error("Error toggling category preferences:", { error: error });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to toggle category preferences"
      };
    }
  }

  /**
   * Enable all notifications
   */
  async enableAllNotifications(): Promise<ServiceResponse<NotificationPreferences>> {
    const updates: NotificationPreferencesUpdate = {
      task_assignments: true,
      task_status_changes: true,
      task_due_reminders: true,
      task_overdue_alerts: true,
      task_comments: true,
      task_mentions: true,
      task_priority_changes: true,
      task_attachments: true,
      subtask_completions: true,
      post_likes: true,
      post_comments: true,
      comment_replies: true,
      community_mentions: true,
      role_changes: true,
      system_announcements: true,
      admin_actions: true,
      xp_milestones: true,
      streak_bonuses: true,
      achievement_badges: true,
      leaderboard_updates: true,
      email_notifications: true,
      push_notifications: true,
      discord_notifications: true,
      whatsapp_notifications: true,
    };

    return await this.updatePreferences(updates);
  }

  async disableAllNotifications(): Promise<ServiceResponse<NotificationPreferences>> {
    const updates: NotificationPreferencesUpdate = {
      task_assignments: true,
      task_status_changes: false,
      task_due_reminders: false,
      task_overdue_alerts: false,
      task_comments: false,
      task_mentions: true,
      task_priority_changes: false,
      task_attachments: false,
      subtask_completions: false,
      post_likes: false,
      post_comments: false,
      comment_replies: false,
      community_mentions: true,
      role_changes: true,
      system_announcements: true,
      admin_actions: false,
      xp_milestones: false,
      streak_bonuses: false,
      achievement_badges: false,
      leaderboard_updates: false,
      email_notifications: false,
      push_notifications: true,
      discord_notifications: false,
      whatsapp_notifications: false,
    };

    return await this.updatePreferences(updates);
  }
}

// Register with service registry
registerService("NotificationPreferencesService", () => new NotificationPreferencesService());

// Export singleton instance
export const notificationPreferencesServiceInstance = new NotificationPreferencesService();

/** Singleton export (Phase 4) — prefer this over the legacy facade shape. */
export const notificationPreferencesService = notificationPreferencesServiceInstance;
